//https://boost.allnetru.dev/api/documentation#/Notifications
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  GetNotificationsResponse,
  GetNotificationPreferencesResponse,
  UpdateNotificationPreferencesRequestBody,
  UpdateNotificationPreferencesResponse,
} from './models';
import { baseQueryWithReauth } from '../baseQueryWithReauth';

const notifications_list = 'notifications_list';
const notifications_preferences = 'notifications_preferences';

export const notificationsApi = createApi({
  reducerPath: notifications_list,
  baseQuery: baseQueryWithReauth,
  tagTypes: [notifications_list, notifications_preferences],
  endpoints: (build) => ({
    getNotifications: build.query<GetNotificationsResponse, { page: number; status?: string }>({
      query: ({ page, status }) => {
        const statusesQueryString = status ? `filters[status]=${status}` : '';
        const pageQueryString = page ? `page=${page}` : '';
        const queryString = [statusesQueryString, pageQueryString].filter(Boolean).join('&');
        return {
          url: `/notifications/${queryString.length ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [notifications_list],
    }),
    getNotificationPreferences: build.query<GetNotificationPreferencesResponse, void>({
      query: () => ({
        url: `/notifications/preferences`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [notifications_preferences],
    }),
    patchNotificationPreferences: build.mutation<
      UpdateNotificationPreferencesResponse,
      UpdateNotificationPreferencesRequestBody
    >({
      query: (body) => ({
        url: `/notifications/preferences`,
        method: 'PATCH',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      // Use the response to update cache directly to avoid refetch flicker
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            notificationsApi.util.updateQueryData(
              'getNotificationPreferences',
              undefined,
              (draft) => {
                draft.data = response.data;
              },
            ),
          );
        } catch {
          // noop; errors handled by rtk-query error flow
        }
      },
    }),
    postNotificationsAreRead: build.mutation<null, void>({
      query: () => ({
        method: 'POST',
        url: '/notifications',
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    postNotificationsHandler: build.mutation<null, { hash?: string | null }>({
      query: ({ hash }) => ({
        method: 'POST',
        url: '/notifications/handler/registration_started',
        ...(hash ? { body: hash } : {}),
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationPreferencesQuery,
  usePatchNotificationPreferencesMutation,
  usePostNotificationsAreReadMutation,
  usePostNotificationsHandlerMutation,
} = notificationsApi;
